import { defineStore } from 'pinia';
import { SHOW_ADDONS_AS_LIST, SHOW_IDOS_AS_LIST } from 'src/stores/menu/localStorageKeys';
import {
	GetAllMenuItemsForTheUserQuery,
	GetTheCountOfAllUnfinishedItemsForTheUserQuery,
} from 'src/stores/menu/queries';

import {
	ModernMainMenuItem,
	ModernMainMenuItemRunType,
	UnfinishedElementCount,
	UnfinishedElementCountRunType,
} from 'src/models/menu';

import {
	GetAllMenuItemsForTheUserQueryCallBack,
	GetTheCountOfAllUnfinishedItemsForTheUserQueryCallBack,
} from './callbacks';
import { FetchMenuPayload, MenuStoreState } from './interfaces';

const readStoredBoolean = (storageKey: string): boolean => {
	const storedValue = localStorage.getItem(storageKey);
	if (storedValue === null) {
		return false;
	}

	return /true/i.test(storedValue);
};

const createDefaultState = (): MenuStoreState => ({
	mainMenuItems: [],
	showAddonsAsList: readStoredBoolean(SHOW_ADDONS_AS_LIST),
	showIdosAsList: readStoredBoolean(SHOW_IDOS_AS_LIST),
	unfinishedElementCountList: [],
});

export const useMenuStore = defineStore('menu', {
	actions: {
		async fetchMainMenuItems(payload: FetchMenuPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<Array<ModernMainMenuItem>>({
					callback: new GetAllMenuItemsForTheUserQueryCallBack((mainMenuItems) =>
						this.setMainMenuItems(mainMenuItems)
					),
					createTime: new Date(),
					query: new GetAllMenuItemsForTheUserQuery(),
					responseType: 'json',
					runType: ModernMainMenuItemRunType,
				});
				return;
			}

			this.setMainMenuItems([]);
		},
		async fetchUnfinishedElementCountList(payload: FetchMenuPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<Array<UnfinishedElementCount>>({
					callback: new GetTheCountOfAllUnfinishedItemsForTheUserQueryCallBack((unfinishedElementCountList) =>
						this.setUnfinishedElementCountList(unfinishedElementCountList)
					),
					createTime: new Date(),
					query: new GetTheCountOfAllUnfinishedItemsForTheUserQuery(),
					responseType: 'json',
					runType: UnfinishedElementCountRunType,
				});
				return;
			}

			this.setUnfinishedElementCountList([]);
		},
		setMainMenuItems(payload: Array<ModernMainMenuItem> | null): void {
			this.mainMenuItems = payload ?? [];
		},
		setShowAddonsAsList(payload: boolean): void {
			localStorage.setItem(SHOW_ADDONS_AS_LIST, JSON.stringify(payload));
			this.showAddonsAsList = payload;
		},
		setShowIdosAsList(payload: boolean): void {
			localStorage.setItem(SHOW_IDOS_AS_LIST, JSON.stringify(payload));
			this.showIdosAsList = payload;
		},
		setUnfinishedElementCountList(payload: Array<UnfinishedElementCount> | null): void {
			this.unfinishedElementCountList = payload ?? [];
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
